// "Install app": adding this website to the home screen like an app (public/sw.js and the web app manifest).
// Chrome and Edge offer a prompt (beforeinstallprompt), kept here until the visitor taps Install. iPhones have no
// prompt: there the button explains Safari's "Add to Home Screen". Hidden when already running as an installed app or
// inside the Capacitor mobile app (mobile/).
import { useEffect, useState } from "react";

let deferredPrompt = null;
const listeners = new Set();
const changed = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    changed();
  });
}

const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document);

export const runningAsApp = () =>
  typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true || Boolean(window.Capacitor));

// Called once at start-up (src/index.js). Only in production builds, so development never serves a cached page.
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js").catch(() => {}));
}

// { available, iosHelp, install }: available = there is something to offer; iosHelp = explain Add to Home Screen
export function useInstallApp() {
  const [, render] = useState(0);
  useEffect(() => {
    const listener = () => render((n) => n + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  const installed = runningAsApp();
  return {
    available: !installed && (Boolean(deferredPrompt) || isIOS),
    iosHelp: !installed && !deferredPrompt && isIOS,
    install: async () => {
      if (!deferredPrompt) return false;
      // The browser can refuse the prompt (it was already used, or no tap came first): then nothing happens
      try {
        deferredPrompt.prompt();
      } catch {
        return false;
      }
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      changed();
      return outcome === "accepted";
    },
  };
}
